// SPDX-License-Identifier: AGPL-3.0-or-later

import {Switch} from '@app/features/ui/components/form/Switch';
import styles from '@app/features/ui/components/SwitchGroup.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface SwitchGroupItemProps {
	label: React.ReactNode;
	value: boolean;
	onChange: (value: boolean) => void;
	shortcut?: React.ReactNode;
	disabled?: boolean;
}
export const SwitchGroupItem = observer(({label, value, onChange, shortcut, disabled}: SwitchGroupItemProps) => {
	const handleClick = () => !disabled && onChange(!value);
	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleClick();
		}
	};
	return (
		<div className={styles.item}>
			<div className={styles.itemContent}>
				<FocusRing offset={-2}>
					<button
						type="button"
						tabIndex={disabled ? -1 : 0}
						className={clsx(styles.itemLabel, {
							[styles.disabled]: disabled,
						})}
						onClick={handleClick}
						onKeyDown={handleKeyDown}
					>
						<span className={styles.labelText}>{label}</span>
						{shortcut && <span className={styles.shortcut}>{shortcut}</span>}
					</button>
				</FocusRing>
				<Switch label="" value={value} onChange={onChange} disabled={disabled} />
			</div>
		</div>
	);
});
interface SwitchGroupProps {
	children: React.ReactNode;
}
export const SwitchGroup = observer(({children}: SwitchGroupProps) => {
	return <div className={styles.container}>{children}</div>;
});
interface SwitchGroupCustomItemProps {
	label: React.ReactNode;
	value: boolean;
	onChange: (value: boolean) => void;
	disabled?: boolean;
	extraContent?: React.ReactNode;
}
export const SwitchGroupCustomItem = observer(
	({label, value, onChange, disabled, extraContent}: SwitchGroupCustomItemProps) => {
		return (
			<div className={clsx('group', styles.item)}>
				<div className={styles.itemContent}>
					<div className={styles.itemLabel}>
						<span className={styles.labelText}>{label}</span>
					</div>
					<div className={styles.extraContent}>
						{extraContent}
						<Switch label="" value={value} onChange={onChange} disabled={disabled} />
					</div>
				</div>
			</div>
		);
	},
);
